import { useSearchParams } from "@/lib/router-compat";
import { getEmployerNameFromParams } from "./employerParam";

interface BcbsilEmpathyAndQuizProps {
  sessionImg?: string;
}

export function BcbsilEmpathyAndQuiz({
  sessionImg,
}: BcbsilEmpathyAndQuizProps = {}) {
  const [searchParams] = useSearchParams();
  const employer = getEmployerNameFromParams(searchParams);

  return (
    <section className="py-14 sm:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Column — Empathy (Problem → Solution) */}
          <div className="lg:col-span-6 space-y-6 text-left">
            <h2 className="text-2xl sm:text-3xl font-semibold text-primary tracking-tight leading-snug">
              {employer
                ? `You've done everything you were supposed to do, ${employer} team member.`
                : "You've done everything you were supposed to do."}
            </h2>

            <div className="space-y-4 text-base sm:text-lg text-foreground leading-relaxed">
              <p>
                You took the medication. You went to therapy. You kept showing
                up for work and for your family. When depression still
                won&apos;t lift, it&apos;s easy to feel like you&apos;ve run out
                of options.
              </p>
              <p className="font-medium text-primary">
                You haven&apos;t. SAINT was built for exactly this moment, and
                it takes one week, not months.
              </p>
            </div>

            {sessionImg && (
              <div className="pt-2">
                <img
                  src={sessionImg}
                  alt="Outpatient personalized TMS session at Neuro Wellness"
                  className="w-full rounded-2xl border border-border shadow-md object-cover aspect-16/10"
                />
              </div>
            )}
          </div>

          {/* Right Column — Quiz Card */}
          <div className="lg:col-span-6" id="saint-quiz">
            <div className="rounded-2xl p-6 sm:p-8 bg-muted border border-border shadow-sm text-left">
              <span className="inline-block text-xs uppercase tracking-wider font-semibold text-accent mb-2">
                Candidate Eligibility
              </span>
              <h2 className="text-2xl sm:text-3xl font-semibold text-primary tracking-tight mb-2">
                Could SAINT be right for you?
              </h2>
              <p className="text-sm sm:text-base text-foreground mb-6">
                Five quick questions. Your answers stay on this page until you
                choose to send them.
              </p>

              {/* Interactive five-question SAINT eligibility quiz */}
              <div
                id="saint-quiz-container"
                className="my-4 rounded-xl border-2 border-dashed border-primary/25 bg-white p-6 text-center space-y-3"
              >
                <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  Interactive Assessment
                </div>
                <p className="text-sm text-primary font-medium">
                  Five quick questions to see if SAINT® may be right for you.
                </p>
                <p className="text-xs text-muted-foreground">
                  Your answers stay on this page until you choose to send them.
                </p>
              </div>

              {/* Fine print */}
              <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
                This quiz is for information only and isn&apos;t a diagnosis. A
                clinician confirms eligibility during your consultation.{" "}
                <a
                  href="/am-i-a-candidate"
                  className="underline text-primary hover:text-accent"
                >
                  Learn about candidate criteria
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
